import type { IComment, IPaginatedResponse } from '@/api';
import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import type {
	InfiniteData,
	UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

// Type for the API hook function
type ApiHook = (
	conversationId: string,
	options: { size: number },
) => UseInfiniteQueryResult<InfiniteData<IPaginatedResponse<IComment>>, Error>;

type RepliesApiHook = (
	commentId: number,
	options: { size: number },
) => UseInfiniteQueryResult<InfiniteData<IPaginatedResponse<IComment>>, Error>;

export const useComment = (useApiHook: ApiHook, conversationId: string) => {
	// Data fetching and transformation
	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useApiHook(conversationId, { size: DEFAULT_PAGE_SIZE });

	const items = useMemo(
		() => data?.pages?.flatMap((page) => page.data) ?? [],
		[data],
	);

	const memoizedFetchNextPage = useCallback(fetchNextPage, []);

	// Infinite scroll handling
	const {
		loadMoreRef,
		isLoading: isInfiniteLoading,
		hasMore,
	} = useInfiniteScroll({
		hasNextPage,
		isLoading,
		isFetchingNextPage,
		fetchNextPage: memoizedFetchNextPage,
	});

	return {
		items,
		isLoading: isInfiniteLoading,
		hasMore,
		loadMoreRef,
		data,
	};
};

export const useCommentReplies = (
	useApiHook: RepliesApiHook,
	commentId: number,
) => {
	// Data fetching and transformation
	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useApiHook(commentId, { size: DEFAULT_PAGE_SIZE });

	const items = useMemo(
		() => data?.pages?.flatMap((page) => page.data) ?? [],
		[data],
	);

	const memoizedFetchNextPage = useCallback(fetchNextPage, []);

	// Infinite scroll handling
	const {
		loadMoreRef,
		isLoading: isInfiniteLoading,
		hasMore,
	} = useInfiniteScroll({
		hasNextPage,
		isLoading,
		isFetchingNextPage,
		fetchNextPage: memoizedFetchNextPage,
	});

	return {
		items,
		isLoading: isInfiniteLoading,
		hasMore,
		loadMoreRef,
		data,
	};
};
